/**
 * Carte de résultat en image, dessinée dans le navigateur.
 *
 * Le partage n'envoyait qu'une phrase et un lien. Or la croissance de ce produit
 * repose entièrement sur le partage — l'acquisition payante ne ferme pas à
 * 3 000 F — et sur WhatsApp, ce qui circule en statut et dans les groupes, c'est
 * une image : un lien se lit, une image se montre.
 *
 * Tout se passe dans le navigateur : aucun appel serveur, aucune dépendance
 * ajoutée, et surtout **aucune donnée nominative** sur la carte. Le nom n'y
 * figure jamais, même quand la personne en a donné un pour son attestation.
 */

/** Côté de la carte. Format carré : c'est celui du statut WhatsApp. */
const COTE = 1080;

const NUIT = "#0b1220";
const NUIT_2 = "#111c2e";
const LAITON = "#c8a24a";
const OS = "#ede9e0";
const OS_DOUX = "#8fa0b8";

export interface CarteParams {
  score: number;
  /** Libellé du gradient, par exemple « Exposition forte ». */
  gradient: string;
  metier: string;
  /** Tâches classées « automatisable aujourd'hui ». */
  high: number;
  /** Nombre total de tâches retenues. */
  total: number;
  /** Hôte affiché en pied de carte. */
  hote: string;
  /**
   * Le chemin court qui ramène ici, et qui crédite celui qui partage.
   *
   * Il **portait délibérément l'hôte seul**, « jamais le lien de parrainage ».
   * La prudence était réelle : une carte qu'on repartage sans y penser
   * emmenait un identifiant avec elle. Mais elle rendait muet l'objet même que
   * ce fichier décrit comme le moteur de la croissance — « un lien se lit, une
   * image se montre » —, et le rendait muet des deux côtés : personne ne
   * savait où aller, personne n'était crédité.
   *
   * Ce que le chemin transporte est un nombre tiré au hasard qui ne désigne
   * personne, il est effaçable depuis les mentions de confidentialité, et il
   * est de toute façon déjà dans le message WhatsApp que la même personne
   * envoie. Le taire sur l'image ne protégeait donc rien qu'elle ne partageait
   * pas déjà — cela empêchait seulement de le compter.
   */
  chemin: string;
}

/**
 * Les polices de la marque viennent d'une feuille distante. Si elles ne sont pas
 * prêtes, le canevas retomberait silencieusement sur une police système et la
 * carte partirait mal composée — on les attend, avec un délai de garde.
 */
async function policesPretes(): Promise<void> {
  if (!("fonts" in document)) return;
  const attente = Promise.all([
    document.fonts.load("700 300px Fraunces"),
    document.fonts.load("600 40px Fraunces"),
    document.fonts.load("500 26px 'IBM Plex Mono'"),
  ]).then(() => undefined);
  await Promise.race([attente, new Promise<void>((ok) => window.setTimeout(ok, 1200))]);
}

/** Applique un interlettrage quand le navigateur le propose. Sans effet sinon. */
function interlettrage(ctx: CanvasRenderingContext2D, valeur: string): void {
  const cible = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ("letterSpacing" in cible) cible.letterSpacing = valeur;
}

/** Découpe un texte en lignes qui tiennent dans `largeur`. */
function lignes(ctx: CanvasRenderingContext2D, texte: string, largeur: number): string[] {
  const mots = texte.split(" ");
  const sortie: string[] = [];
  let courante = "";
  for (const mot of mots) {
    const essai = courante === "" ? mot : `${courante} ${mot}`;
    if (ctx.measureText(essai).width > largeur && courante !== "") {
      sortie.push(courante);
      courante = mot;
    } else {
      courante = essai;
    }
  }
  if (courante !== "") sortie.push(courante);
  return sortie;
}

/**
 * Dessine la carte et rend un PNG. `null` si le navigateur ne sait pas produire
 * de contexte 2D — l'appelant retombe alors sur le partage en texte.
 */
/**
 * Les bornes de l'indice, telles que le barème les fixe.
 *
 * L'échelle de la carte n'est pas décorative : elle situe le chiffre. « 76 »
 * seul ne dit rien à qui reçoit l'image ; « 76 sur une échelle qui va de 22 à
 * 97, marqué ici » se lit d'un coup d'œil, et c'est vrai.
 */
const INDICE_MIN = 22;
const INDICE_MAX = 97;

/**
 * Où se place un indice sur l'échelle, entre 0 et 1.
 *
 * Exportée pour être testée : c'est la seule règle de la carte qui puisse être
 * fausse sans que personne ne le voie. Un repère hors de sa barre ne se
 * remarque pas sur une vignette, et il dirait pourtant quelque chose de faux
 * sur l'image que la personne partage à ses collègues.
 */
export function positionSurEchelle(score: number): number {
  return Math.min(1, Math.max(0, (score - INDICE_MIN) / (INDICE_MAX - INDICE_MIN)));
}

/** Un rectangle aux coins arrondis — le canevas n'en offre pas de primitive. */
function rectangleArrondi(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  largeur: number,
  hauteur: number,
  rayon: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + rayon, y);
  ctx.arcTo(x + largeur, y, x + largeur, y + hauteur, rayon);
  ctx.arcTo(x + largeur, y + hauteur, x, y + hauteur, rayon);
  ctx.arcTo(x, y + hauteur, x, y, rayon);
  ctx.arcTo(x, y, x + largeur, y, rayon);
  ctx.closePath();
}

/**
 * L'échelle de l'indice, avec ses bornes et le repère de la personne.
 *
 * C'est le seul élément graphique de la carte, et il porte une information que
 * le chiffre seul ne porte pas : où l'on se situe. Il est dessiné, non écrit,
 * parce qu'une vignette de statut WhatsApp se regarde avant de se lire.
 */
function echelle(ctx: CanvasRenderingContext2D, y: number, score: number, largeur: number): void {
  const x = 72;
  const hauteur = 10;
  const part = positionSurEchelle(score);

  ctx.fillStyle = "#243350";
  rectangleArrondi(ctx, x, y, largeur, hauteur, hauteur / 2);
  ctx.fill();

  ctx.fillStyle = LAITON;
  rectangleArrondi(ctx, x, y, Math.max(hauteur, largeur * part), hauteur, hauteur / 2);
  ctx.fill();

  // Le repère : un trait vertical net, plus lisible qu'un point une fois
  // l'image réduite à une vignette.
  ctx.fillStyle = OS;
  ctx.fillRect(x + largeur * part - 2, y - 9, 4, hauteur + 18);

  ctx.font = "500 22px 'IBM Plex Mono', ui-monospace, monospace";
  ctx.fillStyle = OS_DOUX;
  ctx.fillText(String(INDICE_MIN), x, y + 46);
  ctx.textAlign = "right";
  ctx.fillText(String(INDICE_MAX), x + largeur, y + 46);
  ctx.textAlign = "left";
}

/**
 * Les tâches, en blocs.
 *
 * « 2 de mes 4 tâches » se lit ; deux carrés pleins sur quatre se **voient**.
 * Au-delà de huit tâches, on ne dessine plus : une rangée de douze carrés ne
 * se compte pas d'un coup d'œil, et la phrase suffit alors.
 */
function blocsTaches(
  ctx: CanvasRenderingContext2D,
  y: number,
  high: number,
  total: number,
): void {
  if (total > 8) return;
  const cote = 26;
  const ecart = 12;
  for (let i = 0; i < total; i += 1) {
    const x = 72 + i * (cote + ecart);
    if (i < high) {
      ctx.fillStyle = LAITON;
      rectangleArrondi(ctx, x, y, cote, cote, 5);
      ctx.fill();
    } else {
      ctx.strokeStyle = "#31425f";
      ctx.lineWidth = 2;
      rectangleArrondi(ctx, x + 1, y + 1, cote - 2, cote - 2, 5);
      ctx.stroke();
    }
  }
}

/**
 * Dessine la carte et rend un PNG. `null` si le navigateur ne sait pas produire
 * de contexte 2D — l'appelant retombe alors sur le partage en texte.
 */
/**
 * Le châssis commun aux cartes — 25/08/2026.
 *
 * Il y a deux cartes désormais : l'indice, partagé après le diagnostic
 * gratuit, et le temps récupéré, offert à l'acheteur. Elles doivent se
 * reconnaître comme sœurs quand elles se croisent dans une même conversation,
 * et c'est le fond, le cadre et la signature qui le font.
 *
 * Écrites deux fois, elles auraient divergé au premier ajustement — la seconde
 * carte n'aurait pas suivi, et deux images presque identiques passent pour du
 * bricolage bien plus sûrement que deux images franchement différentes.
 */
function chassis(): { canevas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const canevas = document.createElement("canvas");
  canevas.width = COTE;
  canevas.height = COTE;
  const ctx = canevas.getContext("2d");
  if (ctx === null) return null;

  ctx.fillStyle = NUIT;
  ctx.fillRect(0, 0, COTE, COTE);

  /*
    Un dégradé très doux depuis le coin supérieur gauche.

    Un aplat de mille quatre-vingts pixels de côté est plat, au sens propre :
    il aplatit tout ce qu'on pose dessus. Le dégradé n'ajoute aucune
    information — il donne une profondeur, et c'est ce qui distingue une image
    composée d'une capture d'écran.
  */
  const fond = ctx.createLinearGradient(0, 0, COTE, COTE);
  fond.addColorStop(0, NUIT_2);
  fond.addColorStop(0.55, NUIT);
  fond.addColorStop(1, "#070d17");
  ctx.fillStyle = fond;
  ctx.fillRect(0, 0, COTE, COTE);

  // Le cadre intérieur : c'est lui qui fait « document » plutôt que « capture ».
  ctx.strokeStyle = "#233047";
  ctx.lineWidth = 2;
  ctx.strokeRect(36, 36, COTE - 72, COTE - 72);

  ctx.textBaseline = "alphabetic";

  // ---- Signature -----------------------------------------------------------
  ctx.font = "500 24px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "4px");
  ctx.fillStyle = LAITON;
  ctx.fillText("STRATERA", 72, 108);
  ctx.fillStyle = OS_DOUX;
  ctx.textAlign = "right";
  ctx.fillText("DIAGNOSTIC D'EXPOSITION À L'IA", COTE - 72, 108);
  ctx.textAlign = "left";
  interlettrage(ctx, "0px");

  ctx.fillStyle = LAITON;
  ctx.fillRect(72, 128, 96, 3);

  /*
    La signature de marque, sous le filet — 24/08/2026.

    Cette carte est le seul canal d'acquisition organique du produit : c'est
    l'image que quelqu'un montre à ses collègues, souvent sans un mot autour.
    Elle doit donc porter ce que la marque dit d'elle-même, et pas seulement son
    nom. Trois mots qui tiennent la méthode entière : on ne juge pas un métier,
    on mesure des tâches.
  */
  ctx.font = "500 17px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "3px");
  ctx.fillStyle = OS_DOUX;
  ctx.fillText("TÂCHE PAR TÂCHE", 72, 158);
  interlettrage(ctx, "0px");

  return { canevas, ctx };
}

export async function dessinerCarte(params: CarteParams): Promise<Blob | null> {
  await policesPretes();
  const socle = chassis();
  if (socle === null) return null;
  const { canevas, ctx } = socle;

  // ---- Le métier, avant le chiffre ----------------------------------------
  /*
    Le métier passe **au-dessus** de l'indice.

    Ce qui fait cliquer celui qui reçoit l'image n'est pas un nombre : c'est de
    reconnaître un métier — le sien, ou celui d'un proche. Le chiffre vient
    juste après, et il est assez grand pour tenir la vignette à lui seul.
  */
  ctx.font = "500 32px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "2px");
  ctx.fillStyle = OS;
  const metier = lignes(ctx, params.metier.toUpperCase(), COTE - 144).slice(0, 2);
  metier.forEach((ligne, i) => ctx.fillText(ligne, 72, 236 + i * 46));
  interlettrage(ctx, "0px");

  // ---- L'indice ------------------------------------------------------------
  /*
    Le bloc du chiffre est posé à une hauteur **fixe**, et le bloc du bas est
    accroché au pied. Tout faire découler du métier, comme avant, faisait
    descendre la carte entière dès qu'un intitulé passait sur deux lignes — et
    la dernière phrase finissait par toucher le filet du pied. Deux ancrages,
    un blanc variable au milieu : c'est le blanc qui absorbe la différence.
  */
  const yScore = 540;
  ctx.font = "700 300px Fraunces, Georgia, serif";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(String(params.score), 68, yScore);
  const largeurScore = ctx.measureText(String(params.score)).width;
  ctx.font = "600 84px Fraunces, Georgia, serif";
  ctx.fillStyle = OS_DOUX;
  ctx.fillText("/100", 82 + largeurScore, yScore);

  ctx.font = "600 58px Fraunces, Georgia, serif";
  ctx.fillStyle = LAITON;
  ctx.fillText(params.gradient, 72, yScore + 94);

  echelle(ctx, yScore + 140, params.score, COTE - 144);

  // ---- Le fait, en toutes lettres -----------------------------------------
  ctx.font = "400 38px Fraunces, Georgia, serif";
  const phrase = `${params.high} de mes ${params.total} tâches principales sont déjà automatisables.`;
  const lignesPhrase = lignes(ctx, phrase, COTE - 144);
  // La dernière ligne s'appuie sur le pied, les précédentes remontent.
  const derniereLigne = 900;
  const premiereLigne = derniereLigne - (lignesPhrase.length - 1) * 52;

  blocsTaches(ctx, premiereLigne - 78, params.high, params.total);

  ctx.fillStyle = OS;
  lignesPhrase.forEach((ligne, i) => ctx.fillText(ligne, 72, premiereLigne + i * 52));

  // ---- Pied ----------------------------------------------------------------
  ctx.fillStyle = "#243350";
  ctx.fillRect(72, COTE - 132, COTE - 144, 1);

  ctx.font = "500 26px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "3px");
  ctx.fillStyle = OS_DOUX;
  ctx.fillText("TESTEZ VOTRE MÉTIER", 72, COTE - 108);
  interlettrage(ctx, "0px");

  /*
    L'adresse sur sa propre ligne, et à gauche.

    Elle était à droite, sur la même ligne que l'invitation, quand elle ne
    portait que l'hôte. Avec le chemin, la ligne dépassait la largeur utile et
    les deux textes se seraient chevauchés — sur une image, un chevauchement ne
    se répare pas : il se publie. Une ligne dédiée coûte trente pixels de pied
    et rend l'adresse recopiable, ce qui est précisément le geste attendu.
  */
  ctx.font = "600 30px 'IBM Plex Mono', ui-monospace, monospace";
  ctx.fillStyle = LAITON;
  ctx.fillText(`${params.hote}${params.chemin}`.toUpperCase(), 72, COTE - 62);

  return new Promise((resoudre) => canevas.toBlob((blob) => resoudre(blob), "image/png"));
}

export interface CarteGainParams {
  /** Heures rendues par semaine, telles que le rapport les annonce. */
  heures: number;
  metier: string;
  hote: string;
  chemin: string;
}

/** « 6,5 » plutôt que « 6.5 » : c'est une image française. */
function heuresLisibles(heures: number): string {
  return heures.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

/**
 * La carte du temps récupéré — offerte à l'acheteur, à lui seul.
 *
 * ## Pourquoi elle existe
 *
 * Ce qui se partageait était un **score**. Un score se compare : « mon indice
 * est de 72 » intrigue une fois, dans un réseau donné, et n'appelle aucun
 * geste. Du temps gagné se convoite — « six heures par semaine » est la seule
 * phrase qu'une personne qui n'a pas encore acheté ait envie de cliquer.
 *
 * ## Pourquoi elle est réservée
 *
 * Le chiffre vient des dix actions du rapport : il n'existe qu'après l'achat.
 * C'est ce qui en fait aussi une raison d'acheter — et ce qui interdit de
 * l'inventer pour les autres. Un « vous pourriez gagner tant » posé sur un
 * diagnostic gratuit serait une promesse de revenu déguisée, et la règle n° 6
 * n'en veut pas plus que des faux témoignages.
 *
 * ## Ce qu'elle ne dit pas
 *
 * Ni francs, ni pourcentage, ni « j'ai gagné ». Elle dit ce que le rapport
 * annonce rendre, au présent d'une mesure et non d'un résultat acquis : le
 * document est un plan, et l'image ne doit pas promettre plus que lui.
 */
export async function dessinerCarteGain(params: CarteGainParams): Promise<Blob | null> {
  await policesPretes();
  const socle = chassis();
  if (socle === null) return null;
  const { canevas, ctx } = socle;

  // ---- Le métier, avant le chiffre, comme sur l'autre carte ---------------
  ctx.font = "500 32px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "2px");
  ctx.fillStyle = OS;
  lignes(ctx, params.metier.toUpperCase(), COTE - 144)
    .slice(0, 2)
    .forEach((ligne, i) => ctx.fillText(ligne, 72, 236 + i * 46));
  interlettrage(ctx, "0px");

  // ---- Les heures ---------------------------------------------------------
  const yChiffre = 470;
  ctx.font = "700 300px Fraunces, Georgia, serif";
  ctx.fillStyle = OS;
  const chiffre = heuresLisibles(params.heures);
  ctx.fillText(chiffre, 72, yChiffre);

  const largeurChiffre = ctx.measureText(chiffre).width;
  ctx.font = "600 88px Fraunces, Georgia, serif";
  ctx.fillStyle = OS_DOUX;
  ctx.fillText("h", 82 + largeurChiffre, yChiffre);

  ctx.font = "600 58px Fraunces, Georgia, serif";
  ctx.fillStyle = LAITON;
  ctx.fillText("par semaine", 72, yChiffre + 94);

  /*
    Un filet plutôt que l'échelle de l'autre carte.

    L'échelle situe un indice entre deux bornes connues. Des heures n'ont pas
    de borne — dire qu'on en a récupéré six « sur quarante » supposerait de
    connaître la semaine de la personne, et l'inventer serait un chiffre faux
    sur une image qui circule.
  */
  ctx.fillStyle = LAITON;
  ctx.fillRect(72, yChiffre + 140, 220, 4);

  // ---- Le fait, en toutes lettres -----------------------------------------
  ctx.font = "400 38px Fraunces, Georgia, serif";
  ctx.fillStyle = OS;
  const phrase = `C'est ce que mon rapport STRATERA annonce me rendre, chaque semaine, tâche par tâche.`;
  const lignesPhrase = lignes(ctx, phrase, COTE - 144);
  const derniereLigne = 900;
  const premiereLigne = derniereLigne - (lignesPhrase.length - 1) * 52;
  lignesPhrase.forEach((ligne, i) => ctx.fillText(ligne, 72, premiereLigne + i * 52));

  // ---- Pied, identique à celui de la carte d'indice ------------------------
  ctx.fillStyle = "#243350";
  ctx.fillRect(72, COTE - 132, COTE - 144, 1);

  ctx.font = "500 26px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "3px");
  ctx.fillStyle = OS_DOUX;
  ctx.fillText("MESUREZ VOTRE MÉTIER", 72, COTE - 108);
  interlettrage(ctx, "0px");

  ctx.font = "600 30px 'IBM Plex Mono', ui-monospace, monospace";
  ctx.fillStyle = LAITON;
  ctx.fillText(`${params.hote}${params.chemin}`.toUpperCase(), 72, COTE - 62);

  return new Promise((resoudre) => canevas.toBlob((blob) => resoudre(blob), "image/png"));
}

export interface CarteProgressionParams {
  /** Heures rendues par semaine, déclarées, selon le rapport de progression. */
  heures: number;
  /** Jours écoulés depuis la ligne de base. */
  jours: number;
  metier: string;
  hote: string;
  chemin: string;
}

/**
 * La phrase de la carte de progression — pure, pour être tenue par un test.
 *
 * Elle dit « je déclare », et c'est tout son objet : ces heures viennent de la
 * personne, deux fois, sur le même barème. STRATERA ne les mesure pas et ne
 * les certifie pas — c'est pourquoi c'est une carte à montrer, et pas une
 * attestation à vérifier (voir `functions/src/attestation.ts`).
 */
export function phraseCarteProgression(jours: number): string {
  return `C'est ce que je déclare avoir repris en ${jours.toLocaleString("fr-FR")} jours, sur les tâches mesurées par STRATERA.`;
}

/** La mention portée en pied de carte, en capitales : elle ne se perd pas au repartage. */
export const MENTION_DECLAREE = "HEURES DÉCLARÉES — NON CERTIFIÉES";

/**
 * La carte de progression — offerte à l'acheteur d'un rapport de progression.
 *
 * La carte du temps récupéré dit ce que le rapport **annonce** rendre ; celle-ci
 * dit ce que la personne **déclare** avoir repris, trois mois plus tard. C'est
 * le premier chiffre du produit qui parle d'un résultat plutôt que d'un plan —
 * et c'est exactement pour cela qu'il porte, sur l'image même, qu'il est
 * déclaré.
 */
export async function dessinerCarteProgression(params: CarteProgressionParams): Promise<Blob | null> {
  await policesPretes();
  const socle = chassis();
  if (socle === null) return null;
  const { canevas, ctx } = socle;

  ctx.font = "500 32px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "2px");
  ctx.fillStyle = OS;
  lignes(ctx, params.metier.toUpperCase(), COTE - 144)
    .slice(0, 2)
    .forEach((ligne, i) => ctx.fillText(ligne, 72, 236 + i * 46));
  interlettrage(ctx, "0px");

  const yChiffre = 470;
  ctx.font = "700 300px Fraunces, Georgia, serif";
  ctx.fillStyle = OS;
  const chiffre = heuresLisibles(params.heures);
  ctx.fillText(chiffre, 72, yChiffre);
  const largeurChiffre = ctx.measureText(chiffre).width;
  ctx.font = "600 88px Fraunces, Georgia, serif";
  ctx.fillStyle = OS_DOUX;
  ctx.fillText("h", 82 + largeurChiffre, yChiffre);

  ctx.font = "600 58px Fraunces, Georgia, serif";
  ctx.fillStyle = LAITON;
  ctx.fillText("rendues par semaine", 72, yChiffre + 94);

  ctx.fillStyle = LAITON;
  ctx.fillRect(72, yChiffre + 140, 220, 4);

  ctx.font = "400 38px Fraunces, Georgia, serif";
  ctx.fillStyle = OS;
  const lignesPhrase = lignes(ctx, phraseCarteProgression(params.jours), COTE - 144);
  const derniereLigne = 860;
  const premiereLigne = derniereLigne - (lignesPhrase.length - 1) * 52;
  lignesPhrase.forEach((ligne, i) => ctx.fillText(ligne, 72, premiereLigne + i * 52));

  ctx.font = "500 24px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "2px");
  ctx.fillStyle = OS_DOUX;
  ctx.fillText(MENTION_DECLAREE, 72, 912);
  interlettrage(ctx, "0px");

  ctx.fillStyle = "#243350";
  ctx.fillRect(72, COTE - 132, COTE - 144, 1);
  ctx.font = "500 26px 'IBM Plex Mono', ui-monospace, monospace";
  interlettrage(ctx, "3px");
  ctx.fillStyle = OS_DOUX;
  ctx.fillText("MESUREZ VOTRE MÉTIER", 72, COTE - 108);
  interlettrage(ctx, "0px");
  ctx.font = "600 30px 'IBM Plex Mono', ui-monospace, monospace";
  ctx.fillStyle = LAITON;
  ctx.fillText(`${params.hote}${params.chemin}`.toUpperCase(), 72, COTE - 62);

  return new Promise((resoudre) => canevas.toBlob((blob) => resoudre(blob), "image/png"));
}

/** Nom de fichier de la carte de progression. */
export function nomCarteProgression(heures: number): string {
  return `progression-${heuresLisibles(heures).replace(",", "-")}h-rendues-par-semaine.png`;
}

/** Nom de fichier de la carte du temps récupéré. */
export function nomCarteGain(heures: number): string {
  return `temps-recupere-${heuresLisibles(heures).replace(",", "-")}h-par-semaine.png`;
}

/** Nom de fichier de la carte enregistrée. */
export function nomCarte(score: number): string {
  return `mon-indice-${score}-sur-100.png`;
}

/**
 * Partage natif avec l'image quand l'appareil le permet — c'est le chemin qui
 * dépose réellement une image dans une conversation ou un statut. Rend `false`
 * si l'appareil ne sait pas, à charge pour l'appelant de retomber sur le texte.
 *
 * L'image doit être **préparée avant le clic** : sur certains navigateurs, un
 * `share()` appelé après une longue attente n'est plus rattaché au geste de
 * l'utilisateur et échoue.
 */
/*
  Le nom du fichier est **donné**, et non déduit d'un score — 25/08/2026.

  Il l'était : `nomCarte(score)`, ce qui allait tant qu'une seule carte
  existait. La carte du temps récupéré serait partie sous le nom
  « mon-indice-6-sur-100.png » pour six heures récupérées — un fichier qui
  ment sur ce qu'il contient, envoyé dans la conversation de quelqu'un
  d'autre. Le nom voyage plus loin que l'image : c'est lui qu'on relit dans
  une liste de fichiers reçus, six mois plus tard.
*/
export async function partagerImage(
  blob: Blob,
  texte: string,
  nomFichier: string,
): Promise<boolean> {
  const fichier = new File([blob], nomFichier, { type: "image/png" });
  const nav = navigator as Navigator & {
    canShare?: (donnees: { files?: File[] }) => boolean;
  };
  if (typeof nav.share !== "function" || nav.canShare?.({ files: [fichier] }) !== true) {
    return false;
  }
  try {
    await nav.share({ text: texte, files: [fichier] });
    return true;
  } catch {
    // Partage refusé ou annulé : ce n'est pas une erreur à signaler.
    return false;
  }
}

/** Enregistre la carte dans les téléchargements. */
export function enregistrerCarte(blob: Blob, score: number): void {
  const url = URL.createObjectURL(blob);
  const lien = document.createElement("a");
  lien.href = url;
  lien.download = nomCarte(score);
  lien.click();
  URL.revokeObjectURL(url);
}
